/**
 * AdminProfessionals Component
 * Page for managing all hairdressers/professionals with full CRUD operations
 */

import React, { Component } from 'react';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import './Admin.css';

class AdminProfessionals extends Component {
    constructor(props) {
        super(props);
        this.state = {
            professionals: [],
            loading: true,
            error: null,
            search: '',
            page: 1,
            limit: 20,
            totalPages: 1,
            totalCount: 0,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            // Modal state
            showModal: false,
            modalMode: 'view',
            selectedProfessional: null,
            editForm: {},
            saving: false
        };
        this.searchTimeout = null;
    }

    componentDidMount() {
        this.fetchProfessionals();
    }

    fetchProfessionals = async () => {
        const { page, limit, search, sortBy, sortOrder } = this.state;
        this.setState({ loading: true });

        try {
            const response = await adminService.getHairdressers({
                page,
                limit,
                search,
                sortBy,
                sortOrder
            });

            if (response.statusCode === 1) {
                this.setState({
                    professionals: response.responseData.hairdressers || [],
                    totalPages: response.responseData.pagination?.pages || 1,
                    totalCount: response.responseData.pagination?.total || 0,
                    loading: false
                });
            } else {
                this.setState({
                    error: 'Failed to load professionals',
                    loading: false
                });
            }
        } catch (error) {
            console.error('Error fetching professionals:', error);
            this.setState({
                error: 'Failed to load professionals',
                loading: false
            });
        }
    }

    handleSearchChange = (e) => {
        const search = e.target.value;
        this.setState({ search, page: 1 });

        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        this.searchTimeout = setTimeout(() => {
            this.fetchProfessionals();
        }, 300);
    }

    handlePageChange = (newPage) => {
        this.setState({ page: newPage }, this.fetchProfessionals);
    }

    handleDelete = async (professionalId, professionalName) => {
        if (!window.confirm(`Are you sure you want to delete professional "${professionalName}"? This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await adminService.deleteHairdresser(professionalId);
            if (response.statusCode === 1) {
                this.fetchProfessionals();
                this.closeModal();
            } else {
                alert('Failed to delete professional');
            }
        } catch (error) {
            console.error('Error deleting professional:', error);
            alert('Failed to delete professional');
        }
    }

    // Modal handlers
    openViewModal = (professional) => {
        this.setState({
            showModal: true,
            modalMode: 'view',
            selectedProfessional: professional,
            editForm: { ...professional }
        });
    }

    openEditModal = (professional) => {
        this.setState({
            showModal: true,
            modalMode: 'edit',
            selectedProfessional: professional,
            editForm: {
                jobTitle: professional.jobTitle || '',
                shortBio: professional.shortBio || '',
                isApproved: professional.isApproved !== false,
                isSalonOwner: professional.isSalonOwner || false,
                employmentTypeId: professional.employmentTypeId || 1
            }
        });
    }

    closeModal = () => {
        this.setState({
            showModal: false,
            modalMode: 'view',
            selectedProfessional: null,
            editForm: {},
            saving: false
        });
    }

    handleEditFormChange = (field, value) => {
        this.setState(prevState => ({
            editForm: {
                ...prevState.editForm,
                [field]: value
            }
        }));
    }

    handleSaveProfessional = async () => {
        const { selectedProfessional, editForm } = this.state;
        this.setState({ saving: true });

        try {
            const response = await adminService.updateHairdresser(selectedProfessional._id, editForm);
            if (response.statusCode === 1) {
                this.fetchProfessionals();
                this.closeModal();
            } else {
                alert('Failed to update professional');
                this.setState({ saving: false });
            }
        } catch (error) {
            console.error('Error updating professional:', error);
            alert('Failed to update professional');
            this.setState({ saving: false });
        }
    }

    getInitials = (name) => {
        if (!name) return 'P';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }

    formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    }

    formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    getSalonName = (professional) => {
        if (professional.salonName) {
            return professional.salonName;
        }
        if (professional.salon) {
            if (typeof professional.salon === 'object') {
                return professional.salon.name || professional.salon.salonName || 'Unknown Salon';
            }
            return 'Linked';
        }
        return 'No Salon';
    }

    getEmploymentType = (typeId) => {
        switch (typeId) {
            case 1: return 'Employed';
            case 2: return 'Self-Employed';
            default: return 'Unknown';
        }
    }

    renderModal() {
        const { showModal, modalMode, selectedProfessional, editForm, saving } = this.state;

        if (!showModal || !selectedProfessional) return null;

        return (
            <div className="admin-modal-overlay" onClick={this.closeModal}>
                <div className="admin-modal" onClick={e => e.stopPropagation()}>
                    <div className="admin-modal-header">
                        <h2>{modalMode === 'edit' ? 'Edit Professional' : 'Professional Details'}</h2>
                        <button className="admin-modal-close" onClick={this.closeModal}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <div className="admin-modal-body">
                        {modalMode === 'view' ? (
                            <div className="admin-detail-grid">
                                <div className="admin-detail-section">
                                    <div className="admin-detail-avatar-large">
                                        {selectedProfessional.userAvatar || selectedProfessional.userImage?.circleImage ? (
                                            <img src={selectedProfessional.userAvatar || selectedProfessional.userImage?.circleImage} alt={selectedProfessional.userName} />
                                        ) : (
                                            <div className="admin-avatar-placeholder-large">
                                                {this.getInitials(selectedProfessional.userName)}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="admin-detail-row">
                                    <label>Name</label>
                                    <span>{selectedProfessional.userName || 'N/A'}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Email</label>
                                    <span>{selectedProfessional.userEmail || 'N/A'}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Phone</label>
                                    <span>{selectedProfessional.userPhone || 'N/A'}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Job Title</label>
                                    <span>{selectedProfessional.jobTitle || 'N/A'}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Salon</label>
                                    <span>{this.getSalonName(selectedProfessional)}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Employment Type</label>
                                    <span>{this.getEmploymentType(selectedProfessional.employmentTypeId)}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Status</label>
                                    <span className={`admin-badge ${selectedProfessional.isApproved !== false ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                        {selectedProfessional.isApproved !== false ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Salon Owner</label>
                                    <span className={`admin-badge ${selectedProfessional.isSalonOwner ? 'admin-badge-info' : 'admin-badge-secondary'}`}>
                                        {selectedProfessional.isSalonOwner ? 'Yes' : 'No'}
                                    </span>
                                </div>
                                {selectedProfessional.shortBio && (
                                    <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                                        <label>Bio</label>
                                        <span style={{ color: '#666' }}>{selectedProfessional.shortBio}</span>
                                    </div>
                                )}
                                <div className="admin-detail-row">
                                    <label>Joined</label>
                                    <span>{this.formatDateTime(selectedProfessional.createdTime)}</span>
                                </div>
                                <div className="admin-detail-row">
                                    <label>Professional ID</label>
                                    <span className="admin-id-text">{selectedProfessional._id}</span>
                                </div>
                            </div>
                        ) : (
                            <div className="admin-form">
                                <div className="admin-form-group">
                                    <label>Job Title</label>
                                    <input
                                        type="text"
                                        value={editForm.jobTitle || ''}
                                        onChange={(e) => this.handleEditFormChange('jobTitle', e.target.value)}
                                        placeholder="e.g., Senior Stylist"
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Bio</label>
                                    <textarea
                                        value={editForm.shortBio || ''}
                                        onChange={(e) => this.handleEditFormChange('shortBio', e.target.value)}
                                        placeholder="Short biography..."
                                        rows="3"
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Employment Type</label>
                                    <select
                                        value={editForm.employmentTypeId || 1}
                                        onChange={(e) => this.handleEditFormChange('employmentTypeId', parseInt(e.target.value))}
                                    >
                                        <option value={1}>Employed</option>
                                        <option value={2}>Self-Employed</option>
                                    </select>
                                </div>
                                <div className="admin-form-group">
                                    <label className="admin-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={editForm.isApproved || false}
                                            onChange={(e) => this.handleEditFormChange('isApproved', e.target.checked)}
                                        />
                                        <span>Active / Approved</span>
                                    </label>
                                </div>
                                <div className="admin-form-group">
                                    <label className="admin-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={editForm.isSalonOwner || false}
                                            onChange={(e) => this.handleEditFormChange('isSalonOwner', e.target.checked)}
                                        />
                                        <span>Salon Owner</span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="admin-modal-footer">
                        {modalMode === 'view' ? (
                            <>
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={() => this.openEditModal(selectedProfessional)}
                                >
                                    Edit Professional
                                </button>
                                <button
                                    className="admin-btn admin-btn-danger"
                                    onClick={() => this.handleDelete(selectedProfessional._id, selectedProfessional.userName)}
                                >
                                    Delete Professional
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="admin-btn admin-btn-secondary"
                                    onClick={() => this.setState({ modalMode: 'view' })}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={this.handleSaveProfessional}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { professionals, loading, error, search, page, totalPages, totalCount } = this.state;

        return (
            <AdminWrapper>
                <div className="admin-page-header">
                    <h1 className="admin-page-title">Professionals</h1>
                    <p className="admin-page-subtitle">Manage all hairdressers and beauty professionals</p>
                </div>

                <div className="admin-table-container">
                    <div className="admin-table-header">
                        <h3 className="admin-table-title">
                            {totalCount} {totalCount === 1 ? 'Professional' : 'Professionals'}
                        </h3>
                        <div className="admin-table-actions">
                            <input
                                type="text"
                                className="admin-search-input"
                                placeholder="Search by name or email..."
                                value={search}
                                onChange={this.handleSearchChange}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <div className="admin-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                            <p style={{ color: '#666' }}>Loading professionals...</p>
                        </div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error}</p>
                            <button className="admin-btn admin-btn-secondary" onClick={this.fetchProfessionals}>
                                Retry
                            </button>
                        </div>
                    ) : professionals.length === 0 ? (
                        <div className="admin-empty-state">
                            <div className="admin-empty-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="48" height="48">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </div>
                            <h3 className="admin-empty-title">No professionals found</h3>
                            <p className="admin-empty-text">
                                {search ? 'Try adjusting your search criteria' : 'No professionals have registered yet'}
                            </p>
                        </div>
                    ) : (
                        <>
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Professional</th>
                                        <th>Email</th>
                                        <th>Salon</th>
                                        <th>Status</th>
                                        <th>Joined</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {professionals.map(professional => (
                                        <tr key={professional._id} onClick={() => this.props.history.push(`/beu-admin-x7k9/professionals/${professional._id}`)} style={{ cursor: 'pointer' }}>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    {professional.userAvatar || professional.userImage?.circleImage ? (
                                                        <img
                                                            src={professional.userAvatar || professional.userImage?.circleImage}
                                                            alt={professional.userName}
                                                            className="admin-table-avatar"
                                                        />
                                                    ) : (
                                                        <div className="admin-table-avatar-placeholder">
                                                            {this.getInitials(professional.userName)}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div style={{ fontWeight: '500' }}>
                                                            {professional.userName || 'Unknown'}
                                                        </div>
                                                        {professional.jobTitle && (
                                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                                {professional.jobTitle}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{professional.userEmail || 'N/A'}</td>
                                            <td>
                                                <span className={`admin-badge ${professional.salonId ? 'admin-badge-info' : 'admin-badge-warning'}`}>
                                                    {this.getSalonName(professional)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`admin-badge ${professional.isApproved !== false ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                                    {professional.isApproved !== false ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>{this.formatDate(professional.createdTime)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {totalPages > 1 && (
                                <div className="admin-pagination">
                                    <div className="admin-pagination-info">
                                        Page {page} of {totalPages} ({totalCount} total)
                                    </div>
                                    <div className="admin-pagination-controls">
                                        <button
                                            className="admin-pagination-btn"
                                            onClick={() => this.handlePageChange(page - 1)}
                                            disabled={page === 1}
                                        >
                                            Previous
                                        </button>
                                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (page <= 3) {
                                                pageNum = i + 1;
                                            } else if (page >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = page - 2 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    className={`admin-pagination-btn ${page === pageNum ? 'active' : ''}`}
                                                    onClick={() => this.handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                        <button
                                            className="admin-pagination-btn"
                                            onClick={() => this.handlePageChange(page + 1)}
                                            disabled={page === totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {this.renderModal()}
            </AdminWrapper>
        );
    }
}

export default AdminProfessionals;
